// Sales stages and the moves between them (04 §4, 02 §4.4).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isConfigured, isDemoMode, supabase } from '../../lib/supabase.js';
import { MOCK_STAGES } from '../../lib/mockData-ops.js';

export interface SalesStage {
  id: number;
  name: string;
  color: string | null;
  position: number;
  is_terminal: boolean;
  terminal_kind: string | null;
}

export interface BoardLead {
  id: number;
  business_name: string;
  area: string | null;
  city_key: string;
  lead_score: number | null;
  tier: string | null;
  sales_stage_id: number | null;
  kanban_order: number | null;
}

const DEMO_BOARD_LEADS: BoardLead[] = [
  {
    id: 1,
    business_name: 'Дентално Студио Д-р Иванова',
    area: 'Лазур',
    city_key: 'burgas',
    lead_score: 92,
    tier: 'A',
    sales_stage_id: 2,
    kanban_order: 1,
  },
  {
    id: 2,
    business_name: 'Дента Център Лазур',
    area: 'Лазур',
    city_key: 'burgas',
    lead_score: 88,
    tier: 'A',
    sales_stage_id: 4,
    kanban_order: 1,
  },
  {
    id: 3,
    business_name: 'Специализирана Клиника Бургас Дент',
    area: 'Център',
    city_key: 'burgas',
    lead_score: 74,
    tier: 'B',
    sales_stage_id: 1,
    kanban_order: 1,
  },
  {
    id: 4,
    business_name: 'Дентална Практика Д-р Георгиев',
    area: 'Възраждане',
    city_key: 'burgas',
    lead_score: 68,
    tier: 'B',
    sales_stage_id: 1,
    kanban_order: 2,
  },
  {
    id: 5,
    business_name: 'Аполония Дентал ООД',
    area: 'Славейков',
    city_key: 'burgas',
    lead_score: 51,
    tier: 'C',
    sales_stage_id: 3,
    kanban_order: 1,
  },
  {
    id: 6,
    business_name: 'Д-р Димитров - Естетична Стоматология',
    area: 'Меден Рудник',
    city_key: 'burgas',
    lead_score: 85,
    tier: 'A',
    sales_stage_id: 5,
    kanban_order: 1,
  },
];

export function useStages() {
  return useQuery({
    queryKey: ['sales_stages'],
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<SalesStage[]> => {
      if (!isConfigured || isDemoMode) {
        return MOCK_STAGES;
      }
      const { data, error } = await supabase
        .from('sales_stages')
        .select('id, name, color, position, is_terminal, terminal_kind')
        .order('position', { ascending: true });
      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as SalesStage[];
    },
  });
}

export function useBoardLeads() {
  return useQuery({
    queryKey: ['board_leads'],
    queryFn: async (): Promise<BoardLead[]> => {
      if (!isConfigured || isDemoMode) {
        return DEMO_BOARD_LEADS;
      }
      const { data, error } = await supabase
        .from('leads')
        .select('id, business_name, area, city_key, lead_score, tier, sales_stage_id, kanban_order')
        .eq('do_not_contact', false)
        .is('parent_lead_id', null)
        .not('business_status', 'in', '("CLOSED_PERMANENTLY","REMOVED_FROM_GOOGLE")')
        .order('kanban_order', { ascending: true, nullsFirst: false })
        .order('lead_score', { ascending: false, nullsFirst: false })
        .limit(2000);
      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as BoardLead[];
    },
  });
}

export function useMoveLead() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      leadId,
      stageId,
      lossReason,
      lossNotes,
    }: {
      leadId: number;
      stageId: number;
      lossReason?: string;
      lossNotes?: string;
    }) => {
      if (!isConfigured || isDemoMode) {
        const item = DEMO_BOARD_LEADS.find((l) => l.id === leadId);
        if (item) item.sales_stage_id = stageId;
        return;
      }
      const { error } = await supabase
        .from('leads')
        .update({ sales_stage_id: stageId })
        .eq('id', leadId);
      if (error) throw new Error(error.message);

      if (lossReason) {
        const { error: outreachErr } = await supabase.from('outreach_events').insert({
          lead_id: leadId,
          channel: 'phone',
          outcome: 'разговор — без интерес',
          loss_reason: lossReason,
          notes: lossNotes && lossNotes.trim() !== '' ? lossNotes.trim() : null,
        });
        if (outreachErr) throw new Error(outreachErr.message);
      }
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['board_leads'] });
      void client.invalidateQueries({ queryKey: ['leads'] });
      void client.invalidateQueries({ queryKey: ['outreach'] });
    },
  });
}
